import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { useTranslation } from '@lib/useTypedTranslation';
import Card from '../../../components/card/card';
import './deviceAnomaliesPage.css';
import { Loading } from '../../../components/loading/loading';
import { InvalidPage } from '../../../components/errors/invalidPage';
import { useWorldRequest } from '../../../lib/useWorldRequest';
import { useWorldRoutes } from '../../../routes/parts/allWorldRoutes';
import { useWorldSettingsContext } from '../../../context/worldSettings';
import { RequestInitWithRetry } from '../../../lib/request';
import { useAlignCharts } from '../../../lib/useAlignCharts';
import { getDeviceInfo } from '../../../services/core/devices';
import { encode } from '../../../lib/base64';
import {
  getDeviceAnomalyEventChart,
  getDeviceAnomalyDataUsageChart,
  IDeviceAnomalyEventDataPoint,
  IDeviceAnomalyDataUsageDataPoint
} from '../../../services/core/deviceAnomaliesCharts';
import { DeviceAnomaliesEventChart } from './compositions/charts/deviceAnomaliesEventChart';
import { DeviceAnomaliesDataUsage } from './compositions/charts/deviceAnomaliesDataUsage';
import { getAnomalyExaminationPath } from './anomalyExaminationPath';
import { getAnomalyDetectionDateRange } from '../../../lib/anomalyDetectionDateRange';

interface IProps {
  deviceId: string
}

type ChartData = [
  IDeviceAnomalyDataUsageDataPoint[],
  IDeviceAnomalyEventDataPoint[],
  IDeviceAnomalyEventDataPoint[],
  IDeviceAnomalyEventDataPoint[],
  IDeviceAnomalyEventDataPoint[],
  IDeviceAnomalyEventDataPoint[]
];

export function DeviceAnomaliesPage(props: IProps) {
  const { deviceId } = props;
  const { t } = useTranslation('anomalyDetectionBeta');
  const { t: tRedirect } = useTranslation('redirect');
  const routes = useWorldRoutes();
  const history = useHistory();
  const { worldSettings } = useWorldSettingsContext();
  const { smartGroupingTimeZone, deviceIdentifier } = worldSettings;
  const timeZone = smartGroupingTimeZone || 'UTC';

  const handleDataUsageDayClick = useCallback((date: number) => {
    history.push(getAnomalyExaminationPath(routes, { deviceId, date, type: 'totalDataUsage' }));
  }, [history, routes, deviceId]);

  const handleLowPowerDayClick = useCallback((date: number) => {
    history.push(getAnomalyExaminationPath(routes, { deviceId, date, type: 'deviceLowPower' }));
  }, [history, routes, deviceId]);

  const handleRebootedDayClick = useCallback((date: number) => {
    history.push(getAnomalyExaminationPath(routes, { deviceId, date, type: 'deviceRebooted' }));
  }, [history, routes, deviceId]);

  const handleDroppedDayClick = useCallback((date: number) => {
    history.push(getAnomalyExaminationPath(routes, { deviceId, date, type: 'deviceDropped' }));
  }, [history, routes, deviceId]);

  const handleProcessHungDayClick = useCallback((date: number) => {
    history.push(getAnomalyExaminationPath(routes, { deviceId, date, type: 'processHung' }));
  }, [history, routes, deviceId]);

  const handleProcessCrashedDayClick = useCallback((date: number) => {
    history.push(getAnomalyExaminationPath(routes, { deviceId, date, type: 'processCrashed' }));
  }, [history, routes, deviceId]);

  const deviceInfoFetcher = useCallback(() => getDeviceInfo(deviceId), [deviceId]);
  const { data: device, loading: deviceLoading } = useWorldRequest(deviceInfoFetcher, { initialLoading: true });

  const anomalyDetectionHour = worldSettings.smartGroupingHour || 0;
  const chartDateRange = useMemo(
    () => getAnomalyDetectionDateRange(timeZone, anomalyDetectionHour),
    [timeZone, anomalyDetectionHour]
  );

  const dataFetcher = useCallback(() => {
    const params = { deviceId, timeZone, ...chartDateRange };
    return (options: RequestInitWithRetry) =>
      Promise.all([
        getDeviceAnomalyDataUsageChart(params)(options),
        getDeviceAnomalyEventChart({ ...params, eventName: 'deviceRebooted' })(options),
        getDeviceAnomalyEventChart({ ...params, eventName: 'deviceDropped' })(options),
        getDeviceAnomalyEventChart({ ...params, eventName: 'deviceLowPower' })(options),
        getDeviceAnomalyEventChart({ ...params, eventName: 'processCrashed' })(options),
        getDeviceAnomalyEventChart({ ...params, eventName: 'processHung' })(options)
      ]) as Promise<ChartData>;
  }, [deviceId, timeZone, chartDateRange]);

  const {
    loading: chartsLoading,
    data: [dataUsageData, rebootsData, dropsData, lowPowerData, processCrashedData, processHungData]
  } = useWorldRequest(dataFetcher, {
    initialLoading: true,
    initialData: [[], [], [], [], [], []] as ChartData
  });

  const chartCount = useMemo(() =>
    [dataUsageData, rebootsData, dropsData, lowPowerData, processCrashedData, processHungData]
      .filter(data => data.some(d => d.isAnomaly)).length,
  [dataUsageData, rebootsData, dropsData, lowPowerData, processCrashedData, processHungData]
  );

  const { addChart, clearCharts } = useAlignCharts(chartCount);

  // Reset chart collection at the start of each new fetch so that alignment fires fresh when new
  // charts mount with the updated data - mirrors dataFetcher's own dependency list exactly
  // (deviceId, timeZone, anomalyDetectionHour) rather than just the subset that usually changes
  // together, so a fetch triggered by anomalyDetectionHour alone (e.g. the world's own
  // smartGroupingHour setting changing while this page is open, with deviceId/timeZone untouched)
  // still clears the collection before the Loading wrapper disposes the outgoing charts - without
  // this, useAlignCharts would still hold the just-disposed chart instances when the incoming ones
  // re-register, throwing when it tries to re-attach a 'ready' listener to a disposed chart.
  useEffect(() => {
    clearCharts();
  }, [deviceId, timeZone, anomalyDetectionHour, clearCharts]);

  const hasNoData = !chartsLoading && !deviceLoading &&
    !dataUsageData.some(d => d.isAnomaly) &&
    !rebootsData.some(d => d.isAnomaly) &&
    !dropsData.some(d => d.isAnomaly) &&
    !lowPowerData.some(d => d.isAnomaly) &&
    !processCrashedData.some(d => d.isAnomaly) &&
    !processHungData.some(d => d.isAnomaly);

  if (hasNoData) {
    return (
      <Card noPadding={true}>
        <InvalidPage
          message={tRedirect('DEVICE_ANOMALY_NO_DATA')}
          redirect={{
            message: tRedirect('REDIRECT_TO_ANOMALY_DETECTION_BETA'),
            path: routes.core.anomalyDetectionBeta
          }}
        />
      </Card>
    );
  }

  return (
    <div data-id="device-anomalies-page">
      <Card noPadding={true}>
        <Loading isLoading={chartsLoading || deviceLoading} transparentOverlay={false} className="device-anomalies-page-loading">
          <div className="device-anomalies-page__header">
            <div className="device-anomalies-page__header-left">
              <h1 className="device-anomalies-page__title">{t('DEVICE_ANOMALY_TITLE')}</h1>
              <div className="device-anomalies-page__breadcrumb">
                <Link to={routes.core.anomalyDetectionBeta} aria-label={t('ANOMALIES_BETA')} className="device-anomalies-page__breadcrumb-link">
                  {t('ANOMALIES_BETA')}
                </Link>
                <i className="fas fa-chevron-right" />
                <span className="device-anomalies-page__device-identifier">{device?.[deviceIdentifier]}</span>
              </div>
            </div>
            <Link
              to={`${routes.core.devicePerformance}?id=${encodeURIComponent(encode(deviceId))}`}
              className="device-anomalies-page__performance-link"
            >
              {t('VIEW_DEVICE_PERFORMANCE')}
            </Link>
          </div>
          {dataUsageData.some(d => d.isAnomaly) && (
            <div className="device-anomalies-page__chart-section">
              <DeviceAnomaliesDataUsage
                data={dataUsageData}
                title={t('TOTAL_DATA_USAGE')}
                cypressId="deviceAnomaliesDataUsage"
                timeZone={timeZone}
                onMount={addChart}
                onDayClick={handleDataUsageDayClick}
              />
            </div>
          )}
          {rebootsData.some(d => d.isAnomaly) && (
            <div className="device-anomalies-page__chart-section">
              <DeviceAnomaliesEventChart
                data={rebootsData}
                title={t('REBOOTS_EVENTS')}
                cypressId="deviceAnomaliesReboots"
                timeZone={timeZone}
                onMount={addChart}
                onDayClick={handleRebootedDayClick}
              />
            </div>
          )}
          {dropsData.some(d => d.isAnomaly) && (
            <div className="device-anomalies-page__chart-section">
              <DeviceAnomaliesEventChart
                data={dropsData}
                title={t('DROPS_EVENTS')}
                cypressId="deviceAnomaliesDrops"
                timeZone={timeZone}
                onMount={addChart}
                onDayClick={handleDroppedDayClick}
              />
            </div>
          )}
          {lowPowerData.some(d => d.isAnomaly) && (
            <div className="device-anomalies-page__chart-section">
              <DeviceAnomaliesEventChart
                data={lowPowerData}
                title={t('LOW_POWER_EVENTS')}
                cypressId="deviceAnomaliesLowPower"
                timeZone={timeZone}
                onMount={addChart}
                onDayClick={handleLowPowerDayClick}
              />
            </div>
          )}
          {processCrashedData.some(d => d.isAnomaly) && (
            <div className="device-anomalies-page__chart-section">
              <DeviceAnomaliesEventChart
                data={processCrashedData}
                title={t('PROCESS_CRASH_EVENTS')}
                cypressId="deviceAnomaliesProcessCrashed"
                timeZone={timeZone}
                onMount={addChart}
                onDayClick={handleProcessCrashedDayClick}
              />
            </div>
          )}
          {processHungData.some(d => d.isAnomaly) && (
            <div className="device-anomalies-page__chart-section">
              <DeviceAnomaliesEventChart
                data={processHungData}
                title={t('PROCESS_HANG_EVENTS')}
                cypressId="deviceAnomaliesProcessHung"
                timeZone={timeZone}
                onMount={addChart}
                onDayClick={handleProcessHungDayClick}
              />
            </div>
          )}
        </Loading>
      </Card>
    </div>
  );
}
